import { useEffect } from 'react';
import { showNotification } from '@mantine/notifications';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { IPaginatedQuery } from '@monorepo/components';

import {
  IAddDomainRateLimitRequest,
  IBotAnalyticsRequest,
  IBotStatusBulkUpdateRequest,
  ICreateChatBotRequest,
  IMessageAllocationRequest,
  ISendMessageRequest,
  IUpdateBotAppearanceRequest,
  IUpdateChatbotNameRequest,
  IUpdateTextPromptRequest,
  IVisibilityRequest,
} from '@/types';
import onApiError from '@/utils/error';

import {
  addChatBot,
  addDomainRateLimit,
  chatbotStatusBulkUpdate,
  chatbotVisibilityBulkUpdate,
  deleteChatbotById,
  fetchBotAnalytics,
  fetchBotById,
  fetchBotImportState,
  fetchChatbotAppearance,
  fetchMessageAllocations,
  fetchPrivateBotWithSession,
  fetchPublicBotById,
  fetchUserAccountSummary,
  fetchUserAnalytics,
  getAllChatBots,
  getAssignedBots,
  sendMessage,
  sendPublicMessage,
  updateBotAppearance,
  updateChatbotName,
  updateChatLimit,
  updateTextPrompt,
} from '../requests/bot.requests';

export const useGetAllChatBots = (query: IPaginatedQuery, options: { enabled?: boolean } = {}) =>
  useInfiniteQuery({
    queryKey: ['bots', query],
    enabled: options.enabled ?? true,
    queryFn: async ({ pageParam = 1 }) => {
      const res = await getAllChatBots({ ...query, page: pageParam });
      return res.data;
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage.pagination) return undefined;
      const nextPage = Number(lastPage.pagination.currentPage) + 1;
      const total = lastPage.pagination.totalPages;

      return nextPage <= total ? nextPage : undefined;
    },
    initialPageParam: 1,
  });

/**
 * Every bot in the list, not just its first page: the remaining pages load in the
 * background, one after another, until the server reports none left. For screens
 * that show the whole list (Manage Bots, the sidebar's bot switcher), which offer
 * no paging of their own and showed only the first page before. Stops after a
 * failed page instead of retrying it in a loop.
 */
/**
 * The bots across every loaded page, each once. Pages are fetched one after another, so a
 * bot added or deleted in between can shift the offsets and repeat a bot at a page
 * boundary; the API's id tie-breaker keeps the order otherwise stable.
 */
export const botsFromPages = <T extends { id: string }>(
  pages: ReadonlyArray<{ docs?: T[] | null }> | undefined
): T[] => {
  const seen = new Set<string>();
  const bots: T[] = [];
  for (const page of pages || []) {
    for (const bot of page.docs || []) {
      if (seen.has(bot.id)) continue;
      seen.add(bot.id);
      bots.push(bot);
    }
  }
  return bots;
};

/**
 * The whole bot list, newest first, unfiltered. Manage Bots (with no search or filter)
 * and the app sidebar use exactly this query so they share one cached list.
 */
export const BOT_LIST_QUERY: IPaginatedQuery = {
  page: 1,
  limit: 50,
  sortBy: 'createdAt',
  sortOrder: 'desc',
  search: '',
};

export const useAllChatBots = (query: IPaginatedQuery, options: { enabled?: boolean } = {}) => {
  const result = useGetAllChatBots(query, options);
  const { hasNextPage, isFetching, isError, fetchNextPage } = result;
  const enabled = options.enabled ?? true;
  // Keyed on the number of loaded pages too: the in-between "fetching" render can be
  // batched away, so after a page lands the other flags can read exactly as they did
  // after the previous one, and the effect would not run again.
  const loadedPages = result.data?.pages.length ?? 0;
  useEffect(() => {
    // Wait out ANY fetch, not just a next-page one: after a change (delete, bulk update)
    // the list is refetched page by page, and asking for the next page meanwhile would
    // cancel that refetch and keep stale pages next to fresh ones. cancelRefetch: false
    // makes a call that does overlap one a no-op instead of a cancellation.
    // Stop on ANY error, not just a failed next page: after a failed refetch the cached
    // pages are stale, and appending fresh pages at their offsets would skip bots. The
    // next successful refetch clears the error and paging resumes.
    if (enabled && hasNextPage && !isFetching && !isError) {
      void fetchNextPage({ cancelRefetch: false });
    }
  }, [enabled, hasNextPage, isFetching, isError, fetchNextPage, loadedPages]);
  return result;
};

// export const useGetAllChatBots = (query: IPaginatedQuery) =>
//   useQuery({
//     queryKey: ['bots', query],
//     queryFn: async () => {
//       const res = await getAllChatBots(query);
//       return res.data;
//     },
//   });

export const useAddChatBot = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: ICreateChatBotRequest) => {
      const res = await addChatBot(payload);
      return res.data;
    },
    onError: () => {
      showNotification({
        title: 'Success',
        message: 'You have reached bot creation limit',
        color: 'red',
      });
    },
    onSuccess: (data) => {
      // Append the new bot to the cached lists in place so the sidebar
      // dropdown shows it instantly, even if the refetch below races a
      // lagging read replica and misses the just-created document.
      const rawCreated = (data as any)?.data?.chatbot ?? (data as any)?.chatbot;
      // The create response is a bare chatbot document — the list endpoint's
      // relations (userFeatureUsage, profile pictures) are absent. Seed them so
      // list consumers indexing into these arrays don't crash on the optimistic
      // entry (MyBotList reads userFeatureUsage[0]).
      const created = rawCreated?.id
        ? {
            userFeatureUsage: [],
            chatBotProfilePictures: [],
            ...rawCreated,
          }
        : rawCreated;
      if (created?.id) {
        queryClient.setQueriesData(
          { predicate: (query) => query.queryKey[0] === 'bots' },
          (oldData: any) => {
            if (!oldData?.pages?.length) return oldData;
            const exists = oldData.pages.some((page: any) =>
              (page.docs || []).some((bot: any) => bot?.id === created.id)
            );
            if (exists) return oldData;
            const [firstPage, ...rest] = oldData.pages;
            return {
              ...oldData,
              pages: [{ ...firstPage, docs: [created, ...(firstPage.docs || [])] }, ...rest],
            };
          }
        );
      }

      queryClient.invalidateQueries({
        predicate: (query) =>
          ['user-analytics', 'bots', 'user', 'feature-usage-status'].includes(
            query.queryKey[0] as string
          ),
      });
      queryClient.refetchQueries({
        predicate: (query) => ['bots', 'assigned-bots'].includes(query.queryKey[0] as string),
      });
    },
  });
};
export const useSendMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: ISendMessageRequest }) => {
      const res = await sendMessage(id, payload);
      return res.data;
    },
    onError: onApiError,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['bot-analytics'],
      });
      // Invalidate user analytics to update dashboard metrics immediately
      queryClient.invalidateQueries({
        queryKey: ['user-analytics'],
      });
    },
  });
};

export const useBotById = (id: string, enabled: boolean, customBaseUrl?: string) =>
  useQuery({
    queryKey: ['bot', id, customBaseUrl],
    queryFn: async () => {
      const res = await fetchBotById(id, customBaseUrl);
      return res.data;
    },
    enabled: !!id && enabled,
    retry: false,
  });

export const useChatbotAppearance = (id: string, customBaseUrl?: string) =>
  useQuery({
    queryKey: ['chatbot-appearance', id, customBaseUrl],
    queryFn: async () => {
      const res = await fetchChatbotAppearance(id, customBaseUrl);
      return res.data;
    },
    enabled: !!id,
    retry: false,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes (appearance rarely changes)
  });

export const useUpdateBotAppearance = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: IUpdateBotAppearanceRequest }) => {
      const res = await updateBotAppearance(id, payload);
      return res.data;
    },
    onError: onApiError,
    onSuccess: (data, variables) => {
      // Invalidate all bot queries to mark them as stale
      queryClient.invalidateQueries({
        predicate: (query) =>
          ['bots', 'bot', 'assigned-bots'].includes(query.queryKey[0] as string),
      });

      // CRITICAL: Force immediate refetch of all bot queries
      // This ensures appearance changes reflect immediately across Dashboard, Conversation, and all pages
      // Even if those pages aren't currently mounted, they'll have fresh data when navigated to
      queryClient.refetchQueries({
        predicate: (query) =>
          ['bots', 'bot', 'assigned-bots'].includes(query.queryKey[0] as string),
      });
    },
  });
};

/** Account-only widgets share this cache; the full dashboard keeps its own response. */
export const useUserAccountSummary = (
  user: { id?: string; type?: string; teamOwnerId?: string | null } | undefined,
  identityResolved: boolean
) =>
  useQuery({
    queryKey: [
      'user-analytics',
      'summary',
      user?.id ?? '',
      user?.type ?? '',
      user?.teamOwnerId ?? '',
    ],
    queryFn: async () => (await fetchUserAccountSummary()).data,
    enabled: identityResolved && !!user?.id && user.type !== 'TEAM_MEMBER',
    staleTime: 60000,
    refetchOnWindowFocus: true,
    refetchInterval: false,
  });

export const useUserAnalytics = () =>
  useQuery({
    queryKey: ['user-analytics'],
    queryFn: async () => {
      const res = await fetchUserAnalytics();
      return res.data;
    },
    staleTime: 60000, // Import progress has its own lightweight poll below.
    refetchOnWindowFocus: true, // Refetch when window regains focus
    // Import progress has its own lightweight poll. Do not rescan the corpus
    // every minute while this dashboard sits open. Focus/invalidation still refresh.
    refetchInterval: false,
    refetchIntervalInBackground: false,
  });

export const useMessageAllocations = () =>
  useQuery({
    queryKey: ['message-allocation'],
    queryFn: async () => {
      const res = await fetchMessageAllocations();
      return res.data;
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    refetchOnWindowFocus: true, // Refetch when user switches back to tab
    // Removed refetchInterval to prevent interrupting user input in modals
  });

/** `scope` (Atlas only) is appended to the key so a cached read belongs to one viewer and owner. */
export const useBotAnalytics = (payload: IBotAnalyticsRequest, enabled = true, scope?: string) =>
  useQuery({
    queryKey: scope
      ? ['bot-analytics', payload, enabled, scope]
      : ['bot-analytics', payload, enabled],
    queryFn: async () => {
      const res = await fetchBotAnalytics(payload);
      return res.data;
    },
    enabled: !!payload.chatbotId && enabled,
    staleTime: 60000,
    refetchOnWindowFocus: true, // Refetch when window regains focus
    // These are aggregate reports, not an ingestion heartbeat. The separate
    // import-state query retains its 15-second progress updates.
    refetchInterval: 60000,
    refetchIntervalInBackground: false,
  });

export const useBotImportState = (id: string, enabled = true) =>
  useQuery({
    queryKey: ['bot-import-state', id],
    queryFn: async () => {
      const res = await fetchBotImportState(id);
      return res.data;
    },
    enabled: !!id && enabled,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: 15000,
  });

export const useAddDomainRateLimit = () =>
  useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: IAddDomainRateLimitRequest }) => {
      const res = await addDomainRateLimit(id, payload);
      return res.data;
    },
    onError: onApiError,
  });

export const useSendPublicMessage = () =>
  useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: ISendMessageRequest }) => {
      const res = await sendPublicMessage(id, payload);
      return res.data;
    },
    onError: onApiError,
  });

export const usePublicBotById = (id: string, applyValidation: boolean, customBaseUrl: string) =>
  useQuery({
    queryKey: ['public-bot', id, applyValidation, customBaseUrl],
    queryFn: async () => {
      const res = await fetchPublicBotById(id, applyValidation, customBaseUrl);
      return res.data;
    },
    enabled: !!id && !!customBaseUrl,
    retry: false,
  });

export const usePrivateBotWithSession = (
  id: string,
  sessionToken: string | null,
  customBaseUrl: string,
  enabled: boolean
) =>
  useQuery({
    queryKey: ['private-bot-session', id, sessionToken, customBaseUrl],
    queryFn: async () => {
      const res = await fetchPrivateBotWithSession(id, sessionToken, customBaseUrl);
      return res.data;
    },
    enabled: !!id && !!customBaseUrl && enabled,
    retry: false,
  });

// export const usePublicBotById = (id: string, applyValidation: boolean) => {
//   const router = useRouter();

//   return useQuery({
//     queryKey: ['public-bot', id],
//     queryFn: async () => {
//       try {
//         const res = await fetchPublicBotById(id, applyValidation);
//         return res.data;
//       } catch (error) {
//         router.push(PageLink.login);
//       }
//     },
//     enabled: !!id,
//   });
// };

export const useChatbotVisibilityBulkUpdate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: IVisibilityRequest) => {
      const res = await chatbotVisibilityBulkUpdate(payload);
      return res.data;
    },
    // REQ-230: No global onApiError here — the call site owns error display
    // to prevent double toasts (one from onApiError + one from the local onError handler).
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) => ['bots', 'user'].includes(query.queryKey[0] as string),
      });
    },
  });
};

export const useChatbotStatusBulkUpdate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: IBotStatusBulkUpdateRequest) => {
      const res = await chatbotStatusBulkUpdate(payload);
      return res.data;
    },
    onError: onApiError,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['bots'],
      });
    },
  });
};

// REQ-231: useDeleteChatbot now passes through the type-to-confirm body and
// surfaces the two possible outcomes (immediate delete vs pending approval)
// to the caller. The mutation result's `data` carries either `chatbot` alone
// (Owner direct-delete) or `chatbot + deletionRequest` (Admin → pending). The
// caller (DangerZone step) decides what to render based on which key is set.
export interface UseDeleteChatbotInput {
  id: string;
  typedChatbotName: string;
  reason?: string;
}

export const useDeleteChatbot = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, typedChatbotName, reason }: UseDeleteChatbotInput) => {
      const res = await deleteChatbotById(id, { typedChatbotName, reason });
      return res.data;
    },
    onError: onApiError,
    onSuccess: (_data, { id }) => {
      // Optimistically remove the deleted bot from all cached lists immediately
      // so it disappears from the UI without waiting for the refetch to complete.
      queryClient.setQueriesData(
        {
          predicate: (query) =>
            ['bots', 'owner-bots', 'assigned-bots'].includes(query.queryKey[0] as string),
        },
        (old: any) => {
          if (!old) return old;
          // infinite query shape (bots): { pages: [{ docs: [] }] }
          if (old.pages) {
            return {
              ...old,
              pages: old.pages.map((page: any) => ({
                ...page,
                docs: page.docs?.filter((bot: any) => bot.id !== id) ?? page.docs,
              })),
            };
          }
          // assigned-bots: cached as a raw array
          if (Array.isArray(old)) {
            return old.filter((bot: any) => bot.id !== id);
          }
          // owner-bots flat shape: { items: [] }
          if (Array.isArray(old.items)) {
            return { ...old, items: old.items.filter((bot: any) => bot.id !== id) };
          }
          return old;
        }
      );
      queryClient.invalidateQueries({
        predicate: (query) =>
          [
            'user-analytics',
            'bots',
            'owner-bots',
            'assigned-bots',
            'user',
            'feature-usage-status',
            'chatbot-deletion-requests',
          ].includes(query.queryKey[0] as string),
      });
    },
  });
};

export const useUpdateTextPrompt = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: IUpdateTextPromptRequest }) => {
      const res = await updateTextPrompt(id, payload);
      return res.data;
    },
    onError: onApiError,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['bot'],
      });
    },
  });
};

export const useUpdateChatLimit = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: IMessageAllocationRequest }) => {
      const res = await updateChatLimit(id, payload);
      return res.data;
    },
    onError: onApiError,
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) =>
          ['user-analytics', 'message-allocation', 'bots', 'bot', 'user'].includes(
            query.queryKey[0] as string
          ),
      });
    },
  });
};

export const useUpdateChatbotName = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: IUpdateChatbotNameRequest }) => {
      const res = await updateChatbotName(id, payload);
      return res.data;
    },
    onError: onApiError,
    onSuccess: (_data, variables) => {
      const { id, payload } = variables;

      // Immediately update the specific bot's cache (instant UI update).
      // useBotById keys are ['bot', id, customBaseUrl], so match on the
      // first two elements rather than an exact (nonexistent) ['bot', id].
      queryClient.setQueriesData(
        {
          predicate: (query) => query.queryKey[0] === 'bot' && query.queryKey[1] === id,
        },
        (oldData: any) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            chatbot: {
              ...oldData.chatbot,
              name: payload.name,
              isPublic: payload.isPublic,
            },
          };
        }
      );

      // Write the rename into every cached bot list in place, so the
      // sidebar dropdown updates instantly and stays correct even if the
      // refetch below returns before the write is visible on a lagging
      // read replica.
      queryClient.setQueriesData(
        { predicate: (query) => query.queryKey[0] === 'bots' },
        (oldData: any) => {
          if (!oldData?.pages) return oldData;
          return {
            ...oldData,
            pages: oldData.pages.map((page: any) => ({
              ...page,
              docs: (page.docs || []).map((bot: any) =>
                bot?.id === id ? { ...bot, name: payload.name, isPublic: payload.isPublic } : bot
              ),
            })),
          };
        }
      );
      queryClient.setQueriesData(
        { predicate: (query) => query.queryKey[0] === 'assigned-bots' },
        (oldData: any) => {
          if (!Array.isArray(oldData)) return oldData;
          return oldData.map((entry: any) => {
            if (entry?.chatbot?.id === id) {
              return { ...entry, chatbot: { ...entry.chatbot, name: payload.name } };
            }
            if (entry?.id === id) {
              return { ...entry, name: payload.name };
            }
            return entry;
          });
        }
      );

      // Invalidate and immediately refetch bot lists for instant sidebar update
      queryClient.invalidateQueries({
        predicate: (query) => ['bots', 'assigned-bots'].includes(query.queryKey[0] as string),
      });
      queryClient.refetchQueries({
        predicate: (query) => ['bots', 'assigned-bots'].includes(query.queryKey[0] as string),
      });
    },
  });
};
// Lightweight one-shot owner bots list for simple pickers (avoids infinite query shape)
export const useOwnerBotsList = (query: IPaginatedQuery) =>
  useQuery({
    queryKey: ['owner-bots', query],
    queryFn: async () => {
      const res = await getAllChatBots(query);
      return res.data;
    },
  });

export const useAssignedBotsList = () =>
  useQuery({
    queryKey: ['assigned-bots'],
    queryFn: async () => {
      const res = await getAssignedBots();
      const items = res.data?.items || [];
      return items;
    },
    staleTime: 30000, // Cache for 30 seconds
    refetchOnWindowFocus: true,
  });
